// Lineup entries + loop data for a set of slots (loaded lazily, cached in the player).
import { useEffect, useState } from "react";
import type { LineupEntry, LoopData } from "@eb/shared";
import type { RiverPlayer } from "./player";
import type { SlotRef } from "./slots";

export interface SlotData {
  entry?: LineupEntry;
  loop?: LoopData;
}

export function useSlotData(player: RiverPlayer | null, slots: SlotRef[], withLoops = true): Map<string, SlotData> {
  const [data, setData] = useState<Map<string, SlotData>>(new Map());
  const sig = `${player?.lineupGeneration ?? 0}|${slots.map((s) => s.key).join(",")}`;
  useEffect(() => {
    if (!player) return;
    let alive = true;
    void (async () => {
      const days = [...new Set(slots.map((s) => s.day))];
      const lineups = new Map(await Promise.all(days.map(async (d) => [d, await player.lineup(d)] as const)));
      const next = new Map<string, SlotData>();
      for (const s of slots) next.set(s.key, { entry: lineups.get(s.day)?.entries[s.slot] });
      if (!alive) return;
      setData(new Map(next));
      if (!withLoops) return;
      const ids = [...next.values()].map((d) => d.entry?.loopId).filter((x): x is string => !!x);
      player.prefetch(ids);
      await Promise.all(
        [...next.entries()].map(async ([k, d]) => {
          if (!d.entry) return;
          const loop = await player.loop(d.entry.loopId);
          next.set(k, { ...d, loop });
        }),
      );
      if (alive) setData(new Map(next));
    })().catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, sig, withLoops]);
  return data;
}
